/**
 * Verbal Reasoning: word relationships, alphabet logic, word puzzles,
 * codes, sequence patterns, comprehension & inference.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/** 1. Word Relationships & Vocabulary - synonyms, antonyms, odd one out, related words */
const generateWordRelationshipsQuestion = (difficulty) => {
    const items = [
        { q: "Which word is a synonym of 'big'?", answer: "Large", options: ["Large", "Small", "Tiny", "Little"], visualType: "synonym-pair", focusWord: "big" },
        { q: "Which word is an antonym of 'brave'?", answer: "Cowardly", options: ["Cowardly", "Bold", "Strong", "Heroic"], visualType: "antonym-pair", focusWord: "brave" },
        { q: "Which word does NOT belong with the others? Happy, Joyful, Sad, Cheerful", answer: "Sad", options: ["Sad", "Happy", "Joyful", "Cheerful"], visualType: "odd-one-out", wordGroup: ["Happy", "Joyful", "Sad", "Cheerful"] },
        { q: "Which word pairs best with 'hot'?", answer: "Cold", options: ["Cold", "Warm", "Fire", "Sun"], visualType: "related-pair", focusWord: "hot" },
        { q: "Which word is closest in meaning to 'difficult'?", answer: "Hard", options: ["Hard", "Easy", "Simple", "Quick"], visualType: "synonym-pair", focusWord: "difficult" },
        { q: "Odd one out: Dog, Cat, Car, Rabbit", answer: "Car", options: ["Car", "Dog", "Cat", "Rabbit"], visualType: "odd-one-out", wordGroup: ["Dog", "Cat", "Car", "Rabbit"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    const { visualType, focusWord, wordGroup } = item;
    return {
        question: item.q,
        answer: item.answer,
        options: shuffle([...item.options]),
        type: "verbal-reasoning",
        mode: "word-relationships",
        visualType: visualType || "word-relationships",
        focusWord,
        wordGroup,
    };
};

/** 2. Alphabet & Letter Logic - sequences, codes, anagrams */
const generateAlphabetLogicQuestion = (difficulty) => {
    const items = [
        { q: "Complete: AB, CD, EF, ___", answer: "GH", options: ["GH", "HI", "IJ", "JK"], visualType: "letter-sequence", sequence: ["AB", "CD", "EF", "?"], step: 2 },
        { q: "What comes next? A, C, E, G, ___", answer: "I", options: ["I", "H", "J", "K"], visualType: "letter-sequence", sequence: ["A", "C", "E", "G", "?"], step: 2 },
        { q: "If A=1, B=2, what is AB?", answer: "3", options: ["3", "12", "21", "4"], visualType: "letter-number-code", codePairs: [["A", "1"], ["B", "2"]], prompt: "A + B = ?" },
        { q: "Rearrange to make a word: TEAR", answer: "Rate", options: ["Rate", "Reta", "Aret", "Trea"], visualType: "anagram", jumbled: "TEAR" },
        { q: "What letter comes before M in the alphabet?", answer: "L", options: ["L", "N", "K", "O"], visualType: "alphabet-position", focusLetter: "M", direction: "before" },
        { q: "Complete the pattern: B, D, F, H, ___", answer: "J", options: ["J", "I", "K", "L"], visualType: "letter-sequence", sequence: ["B", "D", "F", "H", "?"], step: 2 },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    const { visualType, sequence, step, codePairs, prompt, jumbled, focusLetter, direction } = item;
    return {
        question: item.q,
        answer: item.answer,
        options: shuffle([...item.options]),
        type: "verbal-reasoning",
        mode: "alphabet-logic",
        visualType: visualType || "alphabet-logic",
        sequence,
        step,
        codePairs,
        prompt,
        jumbled,
        focusLetter,
        direction,
    };
};

/** 3. Word Puzzles & Formation - hidden word, compound words */
const generateWordPuzzlesQuestion = (difficulty) => {
    const items = [
        { q: "Which word is hidden in 'FATHER'?", answer: "The", options: ["The", "Her", "Hat", "Art"], visualType: "hidden-word", hostWord: "FATHER", hint: "look inside" },
        { q: "Combine 'sun' and 'flower' to make one word:", answer: "Sunflower", options: ["Sunflower", "Flowersun", "Sun-flow", "Flower-sun"], visualType: "compound-word", parts: ["sun", "flower"], symbol: "+" },
        { q: "Which word is hidden in 'COMPLETE'?", answer: "Complete", options: ["Complete", "Comp", "Let", "Ple"], visualType: "hidden-word", hostWord: "COMPLETE" },
        { q: "Combine 'cup' and 'cake' to make one word:", answer: "Cupcake", options: ["Cupcake", "Cakecup", "Cup-cake", "Cake-cup"], visualType: "compound-word", parts: ["cup", "cake"], symbol: "+" },
        { q: "Find a 3-letter word inside 'BREAD':", answer: "Red", options: ["Red", "Ead", "Bad", "Rea"], visualType: "hidden-word", hostWord: "BREAD", hint: "3 letters" },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    const { visualType, hostWord, hint, parts, symbol } = item;
    return {
        question: item.q,
        answer: item.answer,
        options: shuffle([...item.options]),
        type: "verbal-reasoning",
        mode: "word-puzzles",
        visualType: visualType || "word-puzzles",
        hostWord,
        hint,
        parts,
        symbol,
    };
};

/** 4. Codes & Logic - letter-number codes, word codes */
const generateCodesLogicQuestion = (difficulty) => {
    const items = [
        { q: "If A=1, B=2, C=3, what is the value of CAB?", answer: "6", options: ["6", "7", "8", "123"], visualType: "code-chart", codeChart: [["A", "1"], ["B", "2"], ["C", "3"]], codedWord: "CAB", operation: "sum" },
        { q: "If DOG = 4+15+7, what number does DOG equal? (A=1, B=2...)", answer: "26", options: ["26", "15", "22", "20"], visualType: "word-value", word: "DOG", values: [4, 15, 7] },
        { q: "If CAT is coded as 3-1-20, what is DOG? (A=1...Z=26)", answer: "4-15-7", options: ["4-15-7", "4-7-15", "15-4-7", "7-4-15"], visualType: "code-chart", example: "CAT → 3-1-20", codedWord: "DOG" },
        { q: "In a code, RUN becomes SVO (each letter +1). What does SIT become?", answer: "TJU", options: ["TJU", "SIU", "TKT", "UJU"], visualType: "letter-shift", rule: "+1", example: "RUN → SVO" },
        { q: "If 1=A, 2=B, 3=C, what word is 1-2-3?", answer: "ABC", options: ["ABC", "CBA", "123", "One Two Three"], visualType: "number-to-word", codeChart: [["1", "A"], ["2", "B"], ["3", "C"]], code: "1-2-3" },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    const { visualType, codeChart, codedWord, operation, word, values, example, rule, code } = item;
    return {
        question: item.q,
        answer: item.answer,
        options: shuffle([...item.options]),
        type: "verbal-reasoning",
        mode: "codes-logic",
        visualType: visualType || "codes-logic",
        codeChart,
        codedWord,
        operation,
        word,
        values,
        example,
        rule,
        code,
    };
};

/** 5. Sequence & Pattern Logic */
const generateSequencePatternQuestion = (difficulty) => {
    const items = [
        { q: "What comes next? APPLE, BANANA, CHERRY, ___", answer: "DATES", options: ["DATES", "ORANGE", "FRUIT", "APPLE"], visualType: "word-series", series: ["APPLE", "BANANA", "CHERRY", "?"] },
        { q: "Complete: ONE, TWO, THREE, ___", answer: "FOUR", options: ["FOUR", "FIVE", "THREE", "NEXT"], visualType: "word-series", series: ["ONE", "TWO", "THREE", "?"] },
        { q: "Pattern: BIG, BIGGER, ___", answer: "BIGGEST", options: ["BIGGEST", "BIG", "MORE", "MOST"], visualType: "comparative-series", series: ["BIG", "BIGGER", "?"] },
        { q: "What comes next? Z, Y, X, W, ___", answer: "V", options: ["V", "U", "X", "Z"], visualType: "letter-series", series: ["Z", "Y", "X", "W", "?"], direction: "backward" },
        { q: "Complete the series: RUN, RAN, RAN, ___ (past tense)", answer: "RAN", options: ["RAN", "RUN", "RUNNING", "RUNS"], visualType: "word-series", series: ["RUN", "RAN", "RAN", "?"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    const { visualType, series, direction } = item;
    return {
        question: item.q,
        answer: item.answer,
        options: shuffle([...item.options]),
        type: "verbal-reasoning",
        mode: "sequence-pattern",
        visualType: visualType || "sequence-pattern",
        series,
        direction,
    };
};

/** 6. Short Comprehension & Inference */
const generateComprehensionQuestion = (difficulty) => {
    const items = [
        { passage: "Tom wore a coat and carried an umbrella. The sky was grey.", q: "What can we infer about the weather?", answer: "It was likely to rain", options: ["It was likely to rain", "It was sunny", "Tom was cold", "Tom forgot his coat"] },
        { passage: "The baker woke at 4 a.m. to make fresh bread for the shop.", q: "Why did the baker wake early?", answer: "To make bread in time for the shop", options: ["To make bread in time for the shop", "To exercise", "To watch TV", "The shop opens at 4 a.m."] },
        { passage: "Sara put on her swimsuit and ran to the pool.", q: "What was Sara probably about to do?", answer: "Swim", options: ["Swim", "Eat", "Sleep", "Read"] },
    ];
    const item = items[Math.floor(Math.random() * items.length)];
    const question = item.passage ? `${item.passage}\n\n${item.q}` : item.q;
    return {
        question,
        answer: item.answer,
        options: shuffle([...item.options]),
        type: "verbal-reasoning",
        mode: "comprehension",
        visualType: "passage-inference",
        passage: item.passage,
        inferenceQuestion: item.q,
    };
};

export const generateVerbalReasoningQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "vr-word-relationships": return generateWordRelationshipsQuestion(difficulty);
        case "vr-alphabet-logic": return generateAlphabetLogicQuestion(difficulty);
        case "vr-word-puzzles": return generateWordPuzzlesQuestion(difficulty);
        case "vr-codes-logic": return generateCodesLogicQuestion(difficulty);
        case "vr-sequence-pattern": return generateSequencePatternQuestion(difficulty);
        case "vr-comprehension": return generateComprehensionQuestion(difficulty);
        default: return generateWordRelationshipsQuestion(difficulty);
    }
};
